import { Button, message, Modal } from 'antd';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { useUserProvider } from '@/context/UserProvider';
import { fetchInsInviteLink } from '@/lib/service/anonymously';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';

export default function ShareModal({ isModalOpen, onClose }: { isModalOpen: boolean; onClose: () => void }) {
  const [link, setLink] = useState('');
  const { isLogin, onOpenLoginModal } = useUserProvider();
  const [creating, setCreating] = useState(false);
  const { t } = useTranslation();
  const router = useRouter();

  const isReferralPage = router.pathname === '/referral';

  useEffect(() => {
    if (isModalOpen) {
      ga_logEvent(EventName.referral_popup_impression);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (isLogin && isModalOpen) {
      setCreating(true);
      fetchInsInviteLink()
        .then((res) => {
          if (!res.code && res.data?.referral) {
            setLink(`${window.location.origin}/referral?inviteCode=${res.data.referral}`);
          }
        })
        .finally(() => {
          setCreating(false);
        });
    }
  }, [isLogin, isModalOpen]);

  const list = useMemo(() => {
    return [
      {
        icon: '/images/referral/email.svg',
        alt: 'Email',
        name: 'Email',
        onClick(url: string) {
          const subject = encodeURIComponent(t('Try Dolphin Radar — get up to 60 days free'));
          const body = encodeURIComponent(
            t(
              `Hey! I’ve got a special invite that gives you up to 60 days FREE on Dolphin Radar. Just sign up with this link: {{url}}. Explore all the amazing features:)`,
              { url }
            )
          );
          window.location.href = `mailto:?subject=${subject}&body=${body}`;
        },
      },
      {
        icon: '/images/referral/reddit.svg',
        alt: 'Reddit',
        name: 'Reddit',
        onClick(url: string) {
          const title = encodeURIComponent(t('Get 60 Days of Dolphin Radar Free — Limited Referral Invite'));
          // 打开 Reddit 分享窗口
          window.open(
            `https://www.reddit.com/submit?title=${title}&url=${url}`,
            '_blank',
            'noopener,noreferrer,width=800,height=600'
          );
        },
      },
      {
        icon: '/images/referral/facebook.svg',
        alt: 'Facebook',
        name: 'Facebook',
        onClick(url: string) {
          const quote = encodeURIComponent(
            t(
              `🎉 Get up to 60 days FREE on Dolphin Radar! \nUse my invite link now: {{url}}. Don't miss out on this awesome offer! 💥`,
              { url }
            )
          );
          // 打开 Facebook 分享窗口
          window.open(
            `https://www.facebook.com/sharer/sharer.php?quote=${quote}`,
            '_blank',
            'noopener,noreferrer,width=800,height=600'
          );
        },
      },
      {
        icon: '/images/referral/twitter.svg',
        alt: 'Twitter',
        name: 'Twitter',
        onClick(url: string) {
          const text = encodeURIComponent(
            t(
              `Get up to 60 days FREE on Dolphin Radar! Use my invite link now: {{url}}. Don't miss out on this awesome offer!`,
              { url }
            )
          );
          // 打开 Twitter 分享窗口
          window.open(
            `https://twitter.com/intent/tweet?text=${text}`,
            '_blank',
            'noopener,noreferrer,width=800,height=600'
          );
        },
      },
      {
        icon: '/images/referral/whatsapp.svg',
        alt: 'WhatsApp',
        name: 'WhatsApp',
        onClick(url: string) {
          const text = encodeURIComponent(
            t(
              `Get up to 60 days FREE on Dolphin Radar! Use my invite link now: {{url}}. Don't miss out on this awesome offer!`,
              { url }
            )
          );
          // 打开 WhatsApp 分享窗口
          window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer,width=800,height=600');
        },
      },
    ];
  }, [t]);

  const onCopy = () => {
    if (!isLogin) {
      onOpenLoginModal(true);
      return;
    }
    navigator.clipboard.writeText(link);
    message.success('Copied to clipboard');
    ga_logEvent(EventName.referral_popup_copy_link);
  };

  return (
    <Modal
      open={isModalOpen}
      footer={null}
      className="max-w-[436px] rounded-3xl overflow-hidden p-0"
      onCancel={onClose}
      maskClosable={false}
      destroyOnHidden
    >
      <div className="w-full">
        <div className="relative px-[10%] w-[120%] bg-gradient-to-br from-[#FFE9A1] to-75% to-[#E1F4E1] -left-[10%] top-0 rounded-b-[50%]">
          <div className="flex flex-col items-center w-full px-8 max-md:px-4">
            <div className="text-sm py-1.5 px-3 rounded-full bg-white mt-8">
              🎁 <span className="primary-text-60">{t('Invite Your Friends')}</span>
            </div>
            <div className="mt-5 font-bold text-2xl">{t('Get up to 60 days free')}</div>
            <div
              className="mt-4 pb-10 text-center primary-text-60"
              dangerouslySetInnerHTML={{
                __html: t(
                  `Earn <span>+7 days</span> for every friend who subscribes with your link—up to <span>60 days</span> total.`
                ),
              }}
            />
          </div>
        </div>
        <div className="px-8 max-md:px-4 pt-4">
          <div className="text-[#11181A] font-medium">{t('Share your invite link')}</div>
          <div className="flex items-center gap-3 border border-[#D9D9D9] rounded-xl h-[42px] p-1.5 mt-4">
            <input
              type="text"
              placeholder="Your share link will appear here"
              value={link}
              className="flex-1 border-none outline-0 bg-white pl-2.5 py-1 text-ellipsis"
            />
            <Button
              suppressHydrationWarning={false}
              type="primary"
              loading={creating}
              className="flex items-center justify-center text-white h-[30px] px-4 rounded-[6px] bg-[#725CFF] text-xs"
              onClick={onCopy}
            >
              {isLogin ? 'Copy' : 'Create Link'}
            </Button>
          </div>
        </div>
        <div className="px-8 max-md:px-4 pt-10 flex items-center justify-between">
          {list.map((item, index) => (
            <div
              className="flex flex-col flex-1 w-0 items-center cursor-pointer rounded-lg hover:bg-gray-200 p-2 bg-white transition-colors"
              key={index}
              onClick={() => {
                if (isLogin) {
                  item.onClick(link);
                } else {
                  onOpenLoginModal(true);
                }
              }}
            >
              <Image className="block flex-1" src={item.icon} alt={item.alt} width={54} height={54} />
              <div className="text-xs mt-2">{item.name}</div>
            </div>
          ))}
        </div>
        <div className="mt-10 flex-center pb-6">
          {!isReferralPage && (
            <Link className="text-[#3F92FF] text-sm hover:underline" href="/referral">
              {t('View full terms→')}
            </Link>
          )}
        </div>
      </div>
    </Modal>
  );
}
